import {
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Attach20Regular,
  CheckmarkCircle16Regular,
  Dismiss20Regular,
  Send20Regular,
} from '@fluentui/react-icons';
import { brandName, type AttachmentUpload } from '@gandhi/mail-protocol';
import { brandAccent } from '@gandhi-group/ui';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { formatBytes } from '../format.js';
import { useController, useMail } from '../state/context.js';
import { RecipientField } from './RecipientField.js';
import { RichTextEditor, type RichTextCommands } from './RichTextEditor.js';

const AUTOSAVE_IDLE_MS = 1500;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    borderRadius: tokens.borderRadiusLarge,
    background: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow28,
    overflow: 'hidden',
  },
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  bannerText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  brandLine: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold },
  address: { fontSize: tokens.fontSizeBase300, overflowWrap: 'anywhere' },
  saved: { display: 'flex', alignItems: 'center', gap: '4px' },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '12px 16px 16px',
    overflowY: 'auto',
    minHeight: 0,
  },
  fields: { display: 'flex', flexDirection: 'column', gap: '8px' },
  dropping: { outline: `2px dashed ${tokens.colorNeutralStroke1}`, outlineOffset: '-6px' },
  footer: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  attachments: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  // Off-screen rather than display:none, so it stays reachable to assistive
  // technology and to a real file-picker click.
  hidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
  },
});

export interface ComposerProps {
  /** Injected in tests so formatting does not depend on `document.execCommand`. */
  readonly commands?: RichTextCommands;
}

/**
 * Compose says who this is going out as, in words, in a band the width of the pane.
 * There is no control here to change it: the identity comes from the mailbox, and the
 * only way to send as the other brand is to go and stand in that mailbox.
 */
export function Composer(props: ComposerProps): ReactElement | null {
  const styles = useStyles();
  const controller = useController();
  const draft = useMail((s) => s.draft);
  const mailboxes = useMail((s) => s.mailboxes);
  const warnings = useMail((s) => s.warnings);
  const sending = useMail((s) => s.sending);
  const saved = useMail((s) => s.draftSaved);
  const sendError = useMail((s) => s.sendError);
  const mode = useMail((s) => s.themeMode);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [dropping, setDropping] = useState(false);

  // Autosave once the draft has been still for a moment, never mid-keystroke.
  useEffect(() => {
    if (draft === null || saved) return;
    const timer = setTimeout(() => void controller.saveDraft(), AUTOSAVE_IDLE_MS);
    return () => clearTimeout(timer);
  }, [controller, draft, saved]);

  if (draft === null) return null;
  const mailbox = mailboxes.find((m) => m.id === draft.mailboxId);
  if (mailbox === undefined) return null;

  const accent = brandAccent(draft.brandId, mode);
  const brand = brandName(draft.brandId);

  const addFiles = async (files: FileList | null): Promise<void> => {
    if (files === null) return;
    const uploads: AttachmentUpload[] = [];
    for (const file of Array.from(files)) {
      uploads.push({
        name: file.name,
        mimeType: file.type === '' ? 'application/octet-stream' : file.type,
        content: await readAsBase64(file),
      });
    }
    controller.updateDraft({ attachments: [...draft.attachments, ...uploads] });
  };

  return (
    <form
      className={styles.root}
      style={{ border: `3px solid ${accent}` }}
      aria-label={`Compose as ${brand}`}
      // Recipients live in chips, so the field behind them is empty by design and the
      // browser's own required check would refuse to submit. The send button gates it.
      noValidate
      data-brand={draft.brandId}
      data-testid="composer"
      onSubmit={(event) => {
        event.preventDefault();
        void controller.sendDraft();
      }}
    >
      <div className={styles.banner} data-testid="compose-identity">
        <div className={styles.bannerText}>
          <span className={styles.brandLine}>Sending as {brand}</span>
          <span className={styles.address}>{mailbox.address}</span>
        </div>
        {saved && (
          <Caption1 className={styles.saved} role="status" data-testid="draft-saved">
            <CheckmarkCircle16Regular aria-hidden="true" />
            Saved
          </Caption1>
        )}
      </div>

      <div
        className={dropping ? `${styles.body} ${styles.dropping}` : styles.body}
        onDragOver={(event) => {
          event.preventDefault();
          setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDropping(false);
          void addFiles(event.dataTransfer.files);
        }}
      >
        {sendError !== null && (
          <MessageBar intent="error" data-testid="send-error">
            <MessageBarBody>
              <MessageBarTitle>Not sent</MessageBarTitle>
              {sendError}
            </MessageBarBody>
          </MessageBar>
        )}

        {warnings.map((warning) => (
          <MessageBar key={`${warning.kind}-${warning.address}`} intent="warning">
            <MessageBarBody>
              <MessageBarTitle>
                {warning.kind === 'first-contact'
                  ? 'First message to this address'
                  : 'Different brand'}
              </MessageBarTitle>
              {warning.message}
            </MessageBarBody>
          </MessageBar>
        ))}

        <div className={styles.fields}>
          <RecipientField
            label="To"
            required
            value={draft.to}
            warnings={warnings}
            onChange={(to) => controller.updateDraft({ to })}
            onCommit={() => void controller.refreshWarnings()}
          />

          {showCcBcc ? (
            <>
              <RecipientField
                label="Cc"
                value={draft.cc}
                warnings={warnings}
                onChange={(cc) => controller.updateDraft({ cc })}
                onCommit={() => void controller.refreshWarnings()}
              />
              <RecipientField
                label="Bcc"
                value={draft.bcc}
                warnings={warnings}
                onChange={(bcc) => controller.updateDraft({ bcc })}
                onCommit={() => void controller.refreshWarnings()}
              />
            </>
          ) : (
            <Button appearance="transparent" onClick={() => setShowCcBcc(true)}>
              Add Cc and Bcc
            </Button>
          )}

          <Field label="Subject">
            <Input
              value={draft.subject}
              onChange={(_event, data) => controller.updateDraft({ subject: data.value })}
            />
          </Field>
        </div>

        <RichTextEditor
          label="Message body"
          value={draft.bodyHtml}
          onChange={(bodyHtml) => controller.updateDraft({ bodyHtml })}
          {...(props.commands === undefined ? {} : { commands: props.commands })}
        />

        {draft.attachments.length > 0 && (
          <div className={styles.attachments} aria-label="Attached files">
            {draft.attachments.map((attachment, index) => (
              <Button
                key={`${attachment.name}-${index}`}
                size="small"
                appearance="outline"
                icon={<Dismiss20Regular />}
                iconPosition="after"
                aria-label={`Remove attachment ${attachment.name}`}
                onClick={() =>
                  controller.updateDraft({
                    attachments: draft.attachments.filter((_a, i) => i !== index),
                  })
                }
              >
                {attachment.name} ({formatBytes(sizeOf(attachment.content))})
              </Button>
            ))}
          </div>
        )}

        <div className={styles.footer}>
          <Button
            type="submit"
            appearance="primary"
            icon={<Send20Regular />}
            disabled={sending || draft.to.trim() === ''}
            data-testid="send-button"
          >
            Send as {brand}
          </Button>
          <Button
            icon={<Attach20Regular />}
            onClick={() => fileInput.current?.click()}
            type="button"
          >
            Attach
          </Button>
          <input
            ref={fileInput}
            className={styles.hidden}
            type="file"
            multiple
            aria-label="Attach files"
            onChange={(event) => void addFiles(event.currentTarget.files)}
          />
          <Button appearance="subtle" type="button" onClick={() => controller.closeCompose()}>
            Discard
          </Button>
          <Text size={200}>Drop files anywhere in this pane to attach them.</Text>
        </div>
      </div>
    </form>
  );
}

/** base64 expands by four bytes for every three, and pads to a multiple of four. */
function sizeOf(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.max(0, (base64.length * 3) / 4 - padding);
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}
